import React from 'react';
import { HelpCircle } from 'lucide-react';
import { Card } from '@/components/ui/card';

export interface BilingualFAQ {
  q_en: string;
  q_es: string;
  a_en: string;
  a_es: string;
}

interface ExpandedFAQSectionProps {
  faqs: BilingualFAQ[];
  language: 'en' | 'es';
  title?: string;
  description?: string;
  id?: string;
}

/**
 * Always-visible FAQ blocks (no accordion) with FAQPage JSON-LD.
 * Use per-page with topic-specific questions so answers stay indexable.
 */
const ExpandedFAQSection: React.FC<ExpandedFAQSectionProps> = ({
  faqs,
  language,
  title,
  description,
  id = 'faq',
}) => {
  const defaultTitle = language === 'es' ? 'Preguntas Frecuentes' : 'Frequently Asked Questions';
  const defaultDescription = language === 'es'
    ? 'Respuestas a las preguntas más comunes de clientes en Magnolia, Montgomery County, The Woodlands, Conroe y Cypress.'
    : 'Answers to the most common questions from customers in Magnolia, Montgomery County, The Woodlands, Conroe and Cypress.';

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((f) => ({
      '@type': 'Question',
      name: language === 'es' ? f.q_es : f.q_en,
      acceptedAnswer: {
        '@type': 'Answer',
        text: language === 'es' ? f.a_es : f.a_en,
      },
    })),
  };

  return (
    <section id={id} className="py-16 bg-muted/30" aria-labelledby={`${id}-heading`}>
      <div className="container mx-auto px-4">
        <header className="text-center mb-10 max-w-3xl mx-auto">
          <h2 id={`${id}-heading`} className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
            {title || defaultTitle}
          </h2>
          <p className="text-lg text-muted-foreground">
            {description || defaultDescription}
          </p>
        </header>

        <div className="max-w-4xl mx-auto grid gap-4 md:gap-5">
          {faqs.map((faq, index) => (
            <Card key={index} className="p-6 bg-background border">
              <h3 className="flex items-start gap-3 text-lg md:text-xl font-semibold text-foreground mb-3">
                <HelpCircle className="h-5 w-5 md:h-6 md:w-6 text-primary flex-shrink-0 mt-1" aria-hidden="true" />
                <span>{language === 'es' ? faq.q_es : faq.q_en}</span>
              </h3>
              <p className="text-muted-foreground leading-relaxed pl-8">
                {language === 'es' ? faq.a_es : faq.a_en}
              </p>
            </Card>
          ))}
        </div>

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
        />
      </div>
    </section>
  );
};

export default ExpandedFAQSection;
